import { toFiniteNumber } from "./dexaCalculations.js";
import { buildDexaInterpretation } from "./dexaInterpretation.js";

const EPSILON = 0.0001;

export function sortDexaRecords(records) {
  return [...records].sort((left, right) => {
    const leftTime = Date.parse(left?.exam?.date || "") || 0;
    const rightTime = Date.parse(right?.exam?.date || "") || 0;
    return leftTime - rightTime;
  });
}

export function compareValue(previousValue, currentValue) {
  const previous = toFiniteNumber(previousValue);
  const current = toFiniteNumber(currentValue);
  if (previous === null || current === null) {
    return { previous, current, delta: null, percent: null, direction: "unavailable" };
  }
  const delta = current - previous;
  const direction = Math.abs(delta) < EPSILON ? "stable" : delta > 0 ? "up" : "down";
  return {
    previous,
    current,
    delta,
    percent: previous === 0 ? null : delta / Math.abs(previous) * 100,
    direction,
  };
}

export function changeSentence(label, comparison, unit, digits = 1) {
  if (comparison.direction === "unavailable") return `${label}: no hay datos suficientes para comparar.`;
  if (comparison.direction === "stable") return `${label}: se mantuvo estable.`;
  const verb = comparison.direction === "up" ? "aumentó" : "disminuyó";
  const amount = Math.abs(comparison.delta).toLocaleString("es-CO", {
    minimumFractionDigits: digits,
    maximumFractionDigits: digits,
  });
  return `${label}: ${verb} ${amount}${unit}.`;
}

function compareRegion(previous, current) {
  return {
    fatMassG: compareValue(previous?.fatMassG, current?.fatMassG),
    leanMassG: compareValue(previous?.leanMassG, current?.leanMassG),
    bmcG: compareValue(previous?.bmcG, current?.bmcG),
    totalMassG: compareValue(previous?.totalMassG, current?.totalMassG),
    fatPercent: compareValue(previous?.fatPercent, current?.fatPercent),
  };
}

function compareBone(previous, current) {
  return {
    areaCm2: compareValue(previous?.areaCm2, current?.areaCm2),
    bmcG: compareValue(previous?.bmcG, current?.bmcG),
    bmd: compareValue(previous?.bmd, current?.bmd),
    tScore: compareValue(previous?.tScore, current?.tScore),
    zScore: compareValue(previous?.zScore, current?.zScore),
    referenceMeanPercent: compareValue(previous?.referenceMeanPercent, current?.referenceMeanPercent),
    ageMatchedPercent: compareValue(previous?.ageMatchedPercent, current?.ageMatchedPercent),
  };
}

function statusTransition(previousLabel, currentLabel) {
  if (!previousLabel || !currentLabel) return currentLabel || "Interpretación pendiente";
  return previousLabel === currentLabel
    ? `Continúa: ${currentLabel.toLocaleLowerCase("es")}`
    : `Cambió de ${previousLabel.toLocaleLowerCase("es")} a ${currentLabel.toLocaleLowerCase("es")}`;
}

export function buildDexaComparison(records) {
  const orderedRecords = sortDexaRecords(records);
  if (orderedRecords.length < 2) return null;
  const previousRecord = orderedRecords[0];
  const currentRecord = orderedRecords.at(-1);
  const previous = buildDexaInterpretation(previousRecord);
  const current = buildDexaInterpretation(currentRecord);
  const total = compareRegion(previousRecord.composition.total, currentRecord.composition.total);
  const regionKeys = ["leftArm", "rightArm", "trunk", "leftLeg", "rightLeg", "head"];
  const regions = Object.fromEntries(regionKeys.map((key) => [
    key,
    compareRegion(previousRecord.composition.regions[key], currentRecord.composition.regions[key]),
  ]));
  const indexKeys = [
    "bmi", "fmi", "androidGynoidRatio", "trunkLegFatRatio", "trunkExtremityFatRatio",
    "vatMassG", "vatVolumeCm3", "vatAreaCm2", "lmi", "almi",
  ];
  const indices = Object.fromEntries(indexKeys.map((key) => [
    key,
    compareValue(previousRecord.composition.indices[key], currentRecord.composition.indices[key]),
  ]));
  const boneRegionKeys = Object.keys(currentRecord.bone.wholeBodyRegions);
  const wholeBodyRegions = Object.fromEntries(boneRegionKeys.map((key) => [
    key,
    compareBone(previousRecord.bone.wholeBodyRegions[key], currentRecord.bone.wholeBodyRegions[key]),
  ]));
  const hip = {
    neck: compareBone(previousRecord.bone.hip.neck, currentRecord.bone.hip.neck),
    total: compareBone(previousRecord.bone.hip.total, currentRecord.bone.hip.total),
  };
  const lumbar = Object.fromEntries(["l1", "l2", "l3", "l4", "total"].map((key) => [
    key,
    compareBone(previousRecord.bone.lumbar[key], currentRecord.bone.lumbar[key]),
  ]));
  const summary = [
    changeSentence("El porcentaje de grasa", total.fatPercent, " puntos porcentuales"),
    changeSentence("La masa total DXA", total.totalMassG, " g", 0),
    changeSentence("El tejido graso", total.fatMassG, " g", 0),
    changeSentence("El tejido magro", total.leanMassG, " g", 0),
    changeSentence("El contenido mineral óseo", total.bmcG, " g", 1),
    `Actualmente, el porcentaje de grasa se clasifica como ${current.bodyComposition.classification.label.toLocaleLowerCase("es")}.`,
  ].join(" ");
  const symmetrySummary = [
    previous.symmetry.arms.title === current.symmetry.arms.title
      ? `En brazos se mantiene: ${current.symmetry.arms.title.replace(/^Brazos:\s*/i, "").toLocaleLowerCase("es")}.`
      : `En brazos cambió de ${previous.symmetry.arms.title.replace(/^Brazos:\s*/i, "").toLocaleLowerCase("es")} a ${current.symmetry.arms.title.replace(/^Brazos:\s*/i, "").toLocaleLowerCase("es")}.`,
    previous.symmetry.legs.title === current.symmetry.legs.title
      ? `En piernas se mantiene: ${current.symmetry.legs.title.replace(/^Piernas:\s*/i, "").toLocaleLowerCase("es")}.`
      : `En piernas cambió de ${previous.symmetry.legs.title.replace(/^Piernas:\s*/i, "").toLocaleLowerCase("es")} a ${current.symmetry.legs.title.replace(/^Piernas:\s*/i, "").toLocaleLowerCase("es")}.`,
  ].join(" ");
  const adipositySummary = [
    changeSentence("El IMG", indices.fmi, " kg/m²", 2),
    changeSentence("La relación androide/ginoide", indices.androidGynoidRatio, "", 2),
    changeSentence("El tejido adiposo visceral por área", indices.vatAreaCm2, " cm²", 1),
    `Actualmente, el VAT se clasifica como ${current.adiposity.vat.classification.label.toLocaleLowerCase("es")}.`,
  ].join(" ");
  const muscleSummary = [
    changeSentence("El IMM", indices.lmi, " kg/m²", 2),
    changeSentence("El IMMA", indices.almi, " kg/m²", 2),
    `Situación actual: ${current.muscle.imm.note} ${current.muscle.imma.note}`,
  ].join(" ");
  const boneSummary = [
    changeSentence("La DMO de cuerpo entero", compareValue(previousRecord.bone.wholeBody.bmd, currentRecord.bone.wholeBody.bmd), " g/cm²", 3),
    changeSentence("La DMO de cadera total", hip.total.bmd, " g/cm²", 3),
    changeSentence("La DMO de columna L1-L4", lumbar.total.bmd, " g/cm²", 3),
    `La valoración actual se interpreta como: ${current.bone.title.toLocaleLowerCase("es")}.`,
  ].join(" ");

  return {
    records: orderedRecords,
    previous,
    current,
    previousRecord,
    currentRecord,
    total,
    regions,
    indices,
    wholeBody: compareBone(previousRecord.bone.wholeBody, currentRecord.bone.wholeBody),
    wholeBodyRegions,
    hip,
    lumbar,
    summary,
    symmetrySummary,
    adipositySummary,
    muscleSummary,
    boneSummary,
    conclusion: `${summary} ${symmetrySummary} ${adipositySummary} ${muscleSummary} ${boneSummary}`,
    transitions: {
      fat: statusTransition(previous.bodyComposition.classification.label, current.bodyComposition.classification.label),
      bone: statusTransition(previous.bone.title, current.bone.title),
    },
  };
}
